import React, { useState, useEffect, useCallback, useMemo, useRef, useContext } from "react";
import { useHistory } from "react-router-dom";
import {
  Box, Grid, Paper, Typography, Chip, IconButton,
  ThemeProvider, createTheme, CircularProgress,
  Drawer, FormControlLabel, Checkbox, Divider, Tooltip as MuiTooltip
} from "@mui/material";
import { useTheme as useThemeV4 } from "@material-ui/core/styles";
import GroupIcon from "@mui/icons-material/Group";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AllInboxIcon from "@mui/icons-material/AllInbox";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import SettingsIcon from "@mui/icons-material/Settings";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import moment from "moment";
import { toast } from "react-toastify";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";

const STATUS_COLORS = { open: "#3b82f6", pending: "#f59e0b", closed: "#22c55e" };
const REASON_PALETTE = ["#3b82f6", "#22c55e", "#f59e0b", "#ef4444", "#a855f7", "#06b6d4", "#ec4899", "#84cc16"];

// Alerta de SEFAZ no topo do painel (2026-09-28, pedido do usuario:
// "quando o sefaz cair tem como aparecer algum alerta...tipo um pop
// warning" - a notificacao do sininho (NotificationsPopOver) ja existia
// mas e' passiva demais pra uma TV que ninguem fica olhando o tempo
// todo). Reaproveita 100% do monitor que ja existe
// (RunSefazMonitorCheckService / CheckSefazEnvioMonitorService) - so'
// muda como o estado chega na tela.
const SEFAZ_DOC_LABEL = { nfe: "NF-e", nfce: "NFC-e" };
// Prioridade de gravidade pra escolher o pior status entre as duas
// fontes do monitor (certificado + tecnospeed) quando as duas tem
// leitura pro mesmo docType.
const SEFAZ_STATUS_PRIORITY = { erro: 4, timeout: 3, muito_lento: 2, lento: 1, normal: 0 };
const SEFAZ_STATUS_TEXT = {
  erro: "com aviso de erro",
  timeout: "com aviso de timeout",
  muito_lento: "informando lentidão",
  lento: "informando lentidão",
};
const SEFAZ_STATUS_ICON = { erro: "⛔", timeout: "⚠️", muito_lento: "🐢", lento: "🐢" };
// "critical" = vermelho pulsante, "warning" = laranja - mesma logica de
// gravidade que o resto do painel ja usa (SLA estourado = vermelho).
const SEFAZ_STATUS_SEVERITY = { erro: "critical", timeout: "critical", muito_lento: "warning", lento: "warning" };

const MiniPie = ({ data }) => (
  <ResponsiveContainer width="100%" height="100%">
    <PieChart>
      <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius="55%" outerRadius="95%" strokeWidth={1}>
        {data.map((entry, index) => (
          <Cell key={`cell-${index}`} fill={entry.color} />
        ))}
      </Pie>
      <Tooltip />
    </PieChart>
  </ResponsiveContainer>
);

const REFRESH_INTERVAL_MS = 30000;

// Escolha de quais modulos aparecem na tela (pedido do usuario
// 2026-09-08). Guardada por EMPRESA na tabela Setting (chave
// painelTvModules; a coluna value e' TEXT, cabe o JSON) em vez de
// localStorage: a TV fica na parede sem teclado nem mouse, entao a
// configuracao precisa poder ser feita de qualquer maquina e a TV
// pegar sozinha - o que acontece no proximo ciclo de 30s, junto com o
// refresh dos dados. Nao precisou de backend novo: GET /settings/:key
// ja atende qualquer perfil, PUT /settings/:key ja e' admin-only, e o
// UpdateSettingService ja usa findOrCreate.
const MODULES_SETTING_KEY = "painelTvModules";

// A ordem aqui e' a ordem que aparece na engrenagem.
const MODULE_DEFS = [
  { key: "clock", label: "Relogio e data", group: "Topo" },
  { key: "kpiTotalToday", label: "Atendimentos hoje", group: "Cartoes numericos" },
  { key: "kpiPending", label: "Aguardando", group: "Cartoes numericos" },
  { key: "kpiOpen", label: "Em atendimento", group: "Cartoes numericos" },
  { key: "kpiClosed", label: "Encerrados hoje", group: "Cartoes numericos" },
  { key: "kpiPendencias", label: "Pendências", group: "Cartoes numericos" },
  { key: "liveTickets", label: "Atendimentos de hoje (bloco central)", group: "Blocos" },
  { key: "attendants", label: "Quem esta atendendo", group: "Blocos" },
  { key: "statusChart", label: "Status geral", group: "Blocos" },
  { key: "sectorChart", label: "Atendimentos por setor", group: "Blocos" },
  { key: "reasonsChart", label: "Principais motivos", group: "Blocos" },
  { key: "agenda", label: "Agenda de hoje", group: "Blocos" },
  { key: "recurrence", label: "Recorrência hoje", group: "Blocos" },
  { key: "tasks", label: "Tarefas em aberto", group: "Blocos" },
  { key: "aiSummaries", label: "Resumos de IA (encerrados hoje)", group: "Blocos" },
  { key: "pendenciasList", label: "Pendências (lista)", group: "Blocos" }
];

const MODULE_GROUPS = MODULE_DEFS.reduce(
  (acc, m) => (acc.includes(m.group) ? acc : acc.concat(m.group)),
  []
);

const KPI_MODULES = ["kpiTotalToday", "kpiPending", "kpiOpen", "kpiClosed", "kpiPendencias"];

// Peso de cada bloco da linha de baixo na grade. A Agenda e' mais larga
// porque tem mais texto por linha (horario + setor + titulo +
// responsavel) - era o "1.35fr" do gridTemplateColumns fixo de antes.
const BOTTOM_MODULES = [
  { key: "attendants", weight: 1 },
  { key: "statusChart", weight: 1 },
  { key: "sectorChart", weight: 1 },
  { key: "reasonsChart", weight: 1 },
  { key: "agenda", weight: 1.35 },
  { key: "recurrence", weight: 1 },
  // Pedido do usuario 2026-10-02: "preciso conseguir ver de uma forma
  // melhor a tarefa e Pendencia" - mesmo peso de Agenda/Resumos de IA
  // (1.35) em vez de dividir a coluna igual com os graficos, que tem
  // bem menos texto por linha.
  { key: "tasks", weight: 1.35 },
  { key: "aiSummaries", weight: 1.35 },
  { key: "pendenciasList", weight: 1.35 }
];

// Modulo desconhecido conta como VISIVEL: se um modulo novo for criado
// depois de alguem ja ter salvo a configuracao, ele aparece sozinho em
// vez de nascer escondido e parecer quebrado.
const isVisible = (config, key) => config[key] !== false;

const SAVE_DEBOUNCE_MS = 800;

// Fix 2026-08-18: painel feito pra TV precisa se adaptar a qualquer
// resolucao sem depender de saber de antemao qual TV vai usar (1080p,
// 4K, o que for). Em vez de confiar so' nos breakpoints responsivos do
// Grid (que reorganizam o layout mas nao escalam o tamanho visual dos
// elementos - numa TV 4K tudo ficaria pequeno demais, ja que o
// navegador renderiza na resolucao real em pixels), desenha tudo num
// "canvas virtual" de tamanho fixo e escala esse canvas inteiro
// (transform: scale) pra caber exatamente na tela real, mantendo a
// proporcao. Funciona igual em qualquer resolucao.
const DESIGN_WIDTH = 1920;
const DESIGN_HEIGHT = 1080;

const KpiCard = ({ icon, label, value, color }) => (
  <Paper sx={{ p: 1.75, display: "flex", alignItems: "center", gap: 1.75, height: "100%" }}>
    <Box sx={{ color, fontSize: 34, display: "flex" }}>{icon}</Box>
    <Box>
      <Typography variant="h4" fontWeight="bold" lineHeight={1}>{value}</Typography>
      <Typography variant="body1" color="text.secondary">{label}</Typography>
    </Box>
  </Paper>
);

// Os quatro cartoes numericos viraram um mapa por chave de modulo pra
// poderem ser ligados/desligados um a um e a linha se refazer sozinha.
const KPI_CARDS = {
  kpiTotalToday: (s) => (
    <KpiCard icon={<AllInboxIcon fontSize="inherit" />} label="Atendimentos hoje" value={s.totalToday} color="#3b82f6" />
  ),
  kpiPending: (s) => (
    <KpiCard icon={<HourglassEmptyIcon fontSize="inherit" />} label="Aguardando" value={s.pendingCount} color="#f59e0b" />
  ),
  kpiOpen: (s) => (
    <KpiCard icon={<GroupIcon fontSize="inherit" />} label="Em atendimento" value={s.openCount} color="#3b82f6" />
  ),
  kpiClosed: (s) => (
    <KpiCard icon={<CheckCircleIcon fontSize="inherit" />} label="Encerrados hoje" value={s.closedCount} color="#22c55e" />
  ),
  kpiPendencias: (s) => (
    <KpiCard icon={<FlagOutlinedIcon fontSize="inherit" />} label="Pendências" value={s.kanbanPendingCount || 0} color="#f4511e" />
  )
};

const formatElapsed = (minutes) => {
  const m = Math.round(minutes || 0);
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return h > 0 ? `${h}h ${rest}m` : `${rest}m`;
};

const statusLabel = { open: "Em atendimento", pending: "Aguardando", closed: "Encerrado" };
const statusColor = { open: "success", pending: "warning", closed: "default" };

// CNPJ 2026-09-10: a coluna documentNumber ja existia em
// ContactOrganizations, so' nao vinha no payload nem aparecia aqui.
// Formata se vier so' digitos (14 chars); mostra como veio se ja tiver
// mascara (nao sabemos com certeza como fica salvo).
const formatCnpj = (value) => {
  if (!value) return "";
  const digits = String(value).replace(/\D/g, "");
  if (digits.length !== 14) return value;
  return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
};

// Fix 2026-08-19: primeira tentativa de aumentar a fonte (pedido do
// usuario apos testar na TV com zoom 150%) foi longe de mais - estourou
// o canvas fixo de 1080px de altura e criou barra de rolagem na pagina
// inteira. Recalibrado pra um meio-termo: visivelmente maior que o
// original, mas ainda cabendo sem rolar.
const TicketCard = ({ t, onClick }) => {
  const isSlaBreached = t.status === "pending" && t.elapsedMinutes > (t.slaLimitMinutes || 15);

  return (
  <Paper
    variant="outlined"
    onClick={onClick}
    sx={{
      px: 1.25, py: 1, display: "flex", flexDirection: "column", gap: 0.4,
      cursor: "pointer", transition: "border-color .15s, background-color .15s",
      borderColor: isSlaBreached ? "error.main" : undefined,
      borderWidth: isSlaBreached ? 2 : 1,
      bgcolor: isSlaBreached ? (theme) => (theme.palette.mode === "dark" ? "rgba(239,68,68,.18)" : "#fef2f2") : undefined,
      "&:hover": { borderColor: isSlaBreached ? "error.main" : "primary.main" }
    }}
  >
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body1" fontWeight="bold" noWrap title={t.organizationName || t.contactName || t.contactNumber}>
          {t.organizationName || t.contactName || t.contactNumber || "-"}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Ticket nº {t.ticketId}
          {t.organizationDocument ? ` · CNPJ ${formatCnpj(t.organizationDocument)}` : ""}
        </Typography>
      </Box>
      <Chip
        size="small"
        color={statusColor[t.status] || "default"}
        label={statusLabel[t.status] || t.status}
        sx={{ height: 22, fontSize: 12, fontWeight: 600, flexShrink: 0, "& .MuiChip-label": { px: 1 } }}
      />
    </Box>
    {t.liveSummary && (
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          fontStyle: "italic",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {t.liveSummary}
      </Typography>
    )}
    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
      <Typography variant="body2" color="text.secondary" noWrap>{t.userName || "Sem atendente"}</Typography>
      <Typography variant="body2" fontWeight="bold" sx={{ flexShrink: 0 }}>{formatElapsed(t.elapsedMinutes)}</Typography>
    </Box>
  </Paper>
  );
};

// Card de legenda "grafico ao lado do texto" reaproveitado por Status
// geral / Principais motivos - pedido do usuario 2026-08-19 pra
// economizar altura (antes era grafico em cima, legenda embaixo).
const LegendRow = ({ color, name, value }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
    <Box sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />
    <Typography variant="body2" noWrap>{name}</Typography>
    <Typography variant="body2" fontWeight="bold" sx={{ ml: 0.25 }}>{value}</Typography>
  </Box>
);

// Faixa de alerta do SEFAZ - vermelha pulsante pra timeout/erro, laranja
// pra lentidao. Fora da lista de modulos (MODULE_DEFS) de proposito: e'
// um aviso de "algo esta quebrado", nao um bloco informativo comum, e o
// usuario pediu que apareca sozinho, sem depender de ninguem ter
// lembrado de deixar ligado na engrenagem.
const SefazAlertBanner = ({ alert, now }) => {
  const isCritical = SEFAZ_STATUS_SEVERITY[alert.status] === "critical";
  const elapsed = formatElapsed(now.diff(moment(alert.since), "minutes"));

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        borderRadius: 2,
        px: 2.5,
        py: 1.5,
        color: "#fff",
        bgcolor: isCritical ? "#dc2626" : "#b45309",
        animation: isCritical
          ? "sefazPulseCritical 1.8s ease-in-out infinite"
          : "sefazPulseWarning 1.8s ease-in-out infinite",
      }}
    >
      <Box
        sx={{
          width: 12, height: 12, borderRadius: "50%", bgcolor: "#fff", flexShrink: 0,
          animation: "sefazBlink 1s steps(1) infinite",
        }}
      />
      <Box sx={{ fontSize: 26, lineHeight: 1, flexShrink: 0 }}>{SEFAZ_STATUS_ICON[alert.status] || "⚠️"}</Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 800, fontSize: 17 }}>
          {SEFAZ_DOC_LABEL[alert.docType] || alert.docType} {SEFAZ_STATUS_TEXT[alert.status] || alert.status}
        </Typography>
        {alert.message && (
          <Typography sx={{ fontSize: 13, opacity: 0.92 }} noWrap>{alert.message}</Typography>
        )}
      </Box>
      <Box sx={{ textAlign: "right", flexShrink: 0 }}>
        <Typography sx={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5, opacity: 0.8 }}>Há</Typography>
        <Typography sx={{ fontSize: 18, fontWeight: 800, lineHeight: 1 }}>{elapsed}</Typography>
      </Box>
    </Box>
  );
};

const PainelTv = () => {
  const themeV4 = useThemeV4();
  const history = useHistory();
  const [data, setData] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [now, setNow] = useState(moment());
  const [scale, setScale] = useState(1);
  const [agendaFilterOpen, setAgendaFilterOpen] = useState(false);
  const [agendaFilterQueueId, setAgendaFilterQueueId] = useState(null);
  const [modules, setModules] = useState({});
  const [modulesOpen, setModulesOpen] = useState(false);
  const [sefazAlerts, setSefazAlerts] = useState([]);
  const { user, socket } = useContext(AuthContext);

  // Só admin consegue gravar (PUT /settings/:key e' admin-only no
  // backend) e a configuracao vale pra empresa inteira - entao nem
  // mostra a engrenagem pra quem nao poderia salvar.
  const canEditModules = user?.profile === "admin";

  // O ciclo de 30s nao pode sobrescrever o que a pessoa esta ticando
  // agora, e o save e' debounced. Refs em vez de state pra nao
  // re-renderizar o painel a cada clique.
  const modulesOpenRef = useRef(false);
  const saveTimerRef = useRef(null);
  modulesOpenRef.current = modulesOpen;

  useEffect(() => {
    const updateScale = () => {
      const scaleX = window.innerWidth / DESIGN_WIDTH;
      const scaleY = window.innerHeight / DESIGN_HEIGHT;
      setScale(Math.min(scaleX, scaleY));
    };
    updateScale();
    window.addEventListener("resize", updateScale);
    return () => window.removeEventListener("resize", updateScale);
  }, []);

  // Fix 2026-08-18: usava um tema escuro fixo proprio, ignorando o
  // tema claro/escuro que o resto do app ja usa (mesmo padrao do
  // Dashboard/index.js) - o usuario reportou que ficou "fora do padrao".
  const v5Theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: themeV4.palette.type === "dark" ? "dark" : "light",
          primary: { main: themeV4.palette.primary.main },
          background: {
            default: themeV4.palette.background?.default,
            paper: themeV4.palette.type === "dark" ? "#26292d" : "#ffffff",
          },
        },
        shape: { borderRadius: 10 },
      }),
    [themeV4.palette.type, themeV4.palette.primary.main, themeV4.palette.background]
  );

  const fetchData = useCallback(async () => {
    try {
      const { data: resp } = await api.get("/dashboard/painel-tv");
      setData(resp);
    } catch (err) {
      // Painel roda sozinho numa TV, sem ninguem pra ver um toast de erro -
      // so tenta de novo no proximo ciclo, silenciosamente.
      console.error("Falha ao atualizar painel:", err);
    }
  }, []);

  // Agenda de hoje - Fix 2026-08-19: antes era so' um espaco reservado
  // ("em breve, integracao com Google Calendar"). O backend de
  // compromissos (Appointments + sync com Google Calendar) ja existe
  // agora, entao busca de verdade os compromissos do dia.
  const fetchAppointments = useCallback(async () => {
    try {
      const startDate = moment().startOf("day").toISOString();
      const endDate = moment().endOf("day").toISOString();
      const { data: resp } = await api.get("/appointments", { params: { startDate, endDate } });
      setAppointments(resp);
    } catch (err) {
      console.error("Falha ao atualizar agenda:", err);
    }
  }, []);

  // Estado atual do monitor de SEFAZ (mesma rota que a tela
  // Configuracoes > Monitor SEFAZ ja usa). Escolhe o pior status entre
  // as duas fontes (certificado + tecnospeed) por docType e usa o
  // evento mais recente daquele status pra saber desde quando o
  // problema comecou - a fonte "tecnospeed" nao avisa por socket quando
  // volta ao normal, entao depender so' do socket deixaria a faixa presa
  // ligada; o polling de 30s (igual ao resto do painel) e' o que garante
  // que ela tambem some sozinha.
  const fetchSefazStatus = useCallback(async () => {
    try {
      const { data: resp } = await api.get("/settings-sefaz/monitor", { params: { limit: 20 } });
      const { current, events } = resp || {};
      const alerts = Object.keys(current || {})
        .map((docType) => {
          const sources = current[docType] || {};
          let worstStatus = "normal";
          let worstMessage = "";
          Object.values(sources).forEach((s) => {
            if ((SEFAZ_STATUS_PRIORITY[s.status] || 0) > (SEFAZ_STATUS_PRIORITY[worstStatus] || 0)) {
              worstStatus = s.status;
              worstMessage = s.lastMessage;
            }
          });
          if (worstStatus === "normal") return null;
          const matchingEvent = (events || []).find((e) => e.docType === docType && e.status === worstStatus);
          return {
            docType,
            status: worstStatus,
            message: (matchingEvent && matchingEvent.message) || worstMessage,
            since: (matchingEvent && matchingEvent.occurredAt) || new Date().toISOString(),
          };
        })
        .filter(Boolean);
      setSefazAlerts(alerts);
    } catch (err) {
      // Painel roda sozinho numa TV, sem ninguem pra ver um toast de erro -
      // so tenta de novo no proximo ciclo, silenciosamente.
      console.error("Falha ao atualizar status do SEFAZ:", err);
    }
  }, []);

  const fetchModules = useCallback(async () => {
    // Com a engrenagem aberta, ignora o que vem do servidor - senao o
    // ciclo de 30s apagaria o que a pessoa acabou de ticar.
    if (modulesOpenRef.current) return;
    try {
      const { data: resp } = await api.get(`/settings/${MODULES_SETTING_KEY}`);
      const parsed = resp?.value ? JSON.parse(resp.value) : {};
      setModules(parsed && typeof parsed === "object" ? parsed : {});
    } catch (err) {
      // Setting inexistente (primeira vez) ou JSON corrompido cai no
      // padrao "tudo visivel", que e' exatamente o comportamento de
      // antes desta configuracao existir. Painel roda sozinho numa TV,
      // sem ninguem pra ver um toast de erro.
      console.error("Falha ao carregar módulos do painel:", err);
    }
  }, []);

  const persistModules = useCallback(async (next) => {
    try {
      await api.put(`/settings/${MODULES_SETTING_KEY}`, {
        value: JSON.stringify(next)
      });
    } catch (err) {
      // Aqui SIM vale o toast: quem ticou esta olhando a tela.
      toast.error("Não foi possível salvar a configuração do painel.");
      console.error("Falha ao salvar módulos do painel:", err);
    }
  }, []);

  // Tica -> muda na tela na hora (preview ao vivo) -> grava depois de
  // 800ms parado, pra nao disparar um PUT por clique.
  const toggleModule = useCallback((key) => {
    setModules((prev) => {
      const next = { ...prev, [key]: prev[key] === false };
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => persistModules(next), SAVE_DEBOUNCE_MS);
      return next;
    });
  }, [persistModules]);

  useEffect(() => () => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
  }, []);

  useEffect(() => {
    fetchData();
    fetchAppointments();
    fetchModules();
    fetchSefazStatus();
    const modulesInterval = setInterval(fetchModules, REFRESH_INTERVAL_MS);
    const dataInterval = setInterval(fetchData, REFRESH_INTERVAL_MS);
    const appointmentsInterval = setInterval(fetchAppointments, REFRESH_INTERVAL_MS);
    const sefazInterval = setInterval(fetchSefazStatus, REFRESH_INTERVAL_MS);
    const clockInterval = setInterval(() => setNow(moment()), 1000);
    return () => {
      clearInterval(dataInterval);
      clearInterval(appointmentsInterval);
      clearInterval(clockInterval);
      clearInterval(modulesInterval);
      clearInterval(sefazInterval);
    };
  }, [fetchData, fetchAppointments, fetchModules, fetchSefazStatus]);

  // Reage na hora quando o monitor muda de estado (mesmo evento de
  // socket que ja alimenta o sininho de notificacao,
  // company-{id}-sefaz-alert) em vez de esperar ate 30s do polling -
  // so' usa o evento como gatilho pra reconsultar o estado completo
  // (current + events), nao pro payload sozinho, porque uma unica
  // mensagem de socket nao sabe dizer qual e' o pior status entre as
  // duas fontes nem quando a fonte tecnospeed volta ao normal.
  useEffect(() => {
    if (!socket || !user?.companyId) return;
    const eventName = `company-${user.companyId}-sefaz-alert`;
    socket.on(eventName, fetchSefazStatus);
    return () => socket.off(eventName, fetchSefazStatus);
  }, [socket, user, fetchSefazStatus]);

  const sectorPieData = useMemo(() => {
    if (!data?.perSector) return [];
    return data.perSector
      .map((s) => ({
        name: s.queueName,
        value: Number(s.openCount) + Number(s.pendingCount) + Number(s.closedCount),
        color: s.color || "#3b82f6",
      }))
      .filter((d) => d.value > 0);
  }, [data]);

  const reasonsPieData = useMemo(() => {
    if (!data?.topReasons) return [];
    return data.topReasons.map((r, index) => ({
      name: r.reason,
      value: Number(r.count),
      color: r.color && r.color !== "#9e9e9e" ? r.color : REASON_PALETTE[index % REASON_PALETTE.length],
    }));
  }, [data]);

  const statusPieData = useMemo(() => {
    if (!data?.summary) return [];
    return [
      { name: "Aguardando", value: Number(data.summary.pendingCount), color: STATUS_COLORS.pending },
      { name: "Em atendimento", value: Number(data.summary.openCount), color: STATUS_COLORS.open },
      { name: "Encerrados", value: Number(data.summary.closedCount), color: STATUS_COLORS.closed },
    ].filter((d) => d.value > 0);
  }, [data]);

  const groupedLiveTickets = useMemo(() => {
    const order = [];
    const byQueue = {};
    (data?.liveTickets || []).forEach((t) => {
      const key = t.queueName || "Sem fila";
      if (!byQueue[key]) {
        byQueue[key] = { queueName: key, queueColor: t.queueColor, tickets: [] };
        order.push(key);
      }
      byQueue[key].tickets.push(t);
    });
    return order.map((key) => byQueue[key]);
  }, [data]);

  // Fix 2026-08-19: filtro por setor na agenda, escondido atras de uma
  // engrenagem por padrao (usuario nao quer o filtro ocupando espaco o
  // tempo todo numa tela que roda sozinha na maior parte do tempo). As
  // opcoes vem das proprias filas que ja existem (perSector) - quando
  // uma fila nova for criada (ex: "Certificadora"), aparece aqui sozinha.
  const filteredAppointments = useMemo(() => {
    if (!agendaFilterQueueId) return appointments;
    return appointments.filter((a) => a.queueId === agendaFilterQueueId);
  }, [appointments, agendaFilterQueueId]);

  // Layout recalculado pelo que esta visivel. Antes a linha de KPI era
  // md={3} fixo e a de baixo era "1fr 1fr 1fr 1fr 1.35fr" fixo, entao
  // esconder um cartao deixaria um buraco na tela em vez dos vizinhos
  // crescerem pra ocupar o espaco.
  const visibleKpis = useMemo(
    () => KPI_MODULES.filter((k) => isVisible(modules, k)),
    [modules]
  );
  const visibleBottom = useMemo(
    () => BOTTOM_MODULES.filter((m) => isVisible(modules, m.key)),
    [modules]
  );
  const showLiveTickets = isVisible(modules, "liveTickets");
  const bottomTemplate = visibleBottom.map((m) => `${m.weight}fr`).join(" ");
  const nothingVisible =
    visibleKpis.length === 0 && !showLiveTickets && visibleBottom.length === 0;

  if (!data) {
    return (
      <ThemeProvider theme={v5Theme}>
        <Box sx={{ bgcolor: "background.default", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <CircularProgress size={60} />
        </Box>
      </ThemeProvider>
    );
  }

  const { summary, perSector, attendants, recurrenceToday, openTasks, recentClosedSummaries, kanbanPendingList } = data;

  return (
    <ThemeProvider theme={v5Theme}>
      <style>{`
        @keyframes sefazBlink { 50% { opacity: .25; } }
        @keyframes sefazPulseCritical {
          0%, 100% { box-shadow: 0 0 0 0 rgba(220,38,38,.55); }
          50% { box-shadow: 0 0 0 14px rgba(220,38,38,0); }
        }
        @keyframes sefazPulseWarning {
          0%, 100% { box-shadow: 0 0 0 0 rgba(180,83,9,.55); }
          50% { box-shadow: 0 0 0 14px rgba(180,83,9,0); }
        }
      `}</style>
      <Box sx={{ bgcolor: "background.default", width: "100vw", height: "100vh", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Box
        sx={{
          width: DESIGN_WIDTH,
          height: DESIGN_HEIGHT,
          flexShrink: 0,
          transform: `scale(${scale})`,
          bgcolor: "background.default",
          p: 3,
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Fix 2026-08-19: titulo/relogio reduzidos - usuario pediu pra
            liberar espaco vertical pra Agenda de hoje (que ganhou uma
            posicao propria na linha de baixo, no lugar de Tarefas). */}
        <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 1.25, flexShrink: 0 }}>
          <Grid item>
            <Typography variant="h5" fontWeight="bold">Painel de Atendimento</Typography>
          </Grid>
          <Grid item sx={{ textAlign: "right", display: "flex", alignItems: "center", gap: 1 }}>
            {isVisible(modules, "clock") && (
              <Box sx={{ textAlign: "right" }}>
                <Typography variant="h5" fontWeight="bold">{now.format("HH:mm:ss")}</Typography>
                <Typography variant="body2" color="text.secondary">{now.format("dddd, DD [de] MMMM")}</Typography>
              </Box>
            )}
            {canEditModules && (
              // Quase invisivel por padrao: a tela passa a maior parte
              // do tempo rodando sozinha numa TV e o usuario ja pediu
              // (2026-08-19, no filtro da Agenda) que controle nao
              // ocupasse espaco visual o tempo todo.
              <MuiTooltip title="Escolher módulos do painel">
                <IconButton
                  size="small"
                  onClick={() => setModulesOpen(true)}
                  sx={{ opacity: 0.25, "&:hover": { opacity: 1 } }}
                >
                  <SettingsIcon fontSize="small" />
                </IconButton>
              </MuiTooltip>
            )}
          </Grid>
        </Grid>

        {sefazAlerts.length > 0 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 1.5, flexShrink: 0 }}>
            {sefazAlerts.map((alert) => (
              <SefazAlertBanner key={alert.docType} alert={alert} now={now} />
            ))}
          </Box>
        )}

        {/* Cards numericos - a largura de cada um vem do quanto sobrou:
            12/1=12, 12/2=6, 12/3=4, 12/4=3. */}
        {visibleKpis.length > 0 && (
          <Grid container spacing={2} sx={{ mb: 1.5, flexShrink: 0 }}>
            {visibleKpis.map((key) => (
              <Grid item xs={12} sm={6} md={12 / visibleKpis.length} key={key}>
                {KPI_CARDS[key](summary)}
              </Grid>
            ))}
          </Grid>
        )}

        {nothingVisible && (
          <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Typography variant="h6" color="text.secondary" align="center">
              Nenhum módulo selecionado
              {canEditModules ? " — abra a engrenagem no topo para escolher o que aparece." : "."}
            </Typography>
          </Box>
        )}

        {/* Fix 2026-08-18: usuario pediu pra "atendimento de hoje" ser o
            real destaque, ocupando toda a largura de cima - o resto
            (agenda, status/setor/motivos) vira secundario, empilhado
            abaixo em vez de dividir a tela em colunas. */}
        <Paper sx={{ overflow: "hidden", flex: 1, minHeight: 0, display: "flex", flexDirection: "column", mb: 1.5 }}>
          <Typography variant="h6" fontWeight="bold" sx={{ p: 1.5, pb: 0.5, flexShrink: 0 }}>Atendimentos de hoje</Typography>
          <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden", p: 1.5, pt: 0 }}>
            {groupedLiveTickets.length === 0 ? (
              <Typography variant="body1" color="text.secondary" align="center" sx={{ py: 3 }}>
                Nenhum atendimento hoje ainda.
              </Typography>
            ) : (
              <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
                {groupedLiveTickets.map((g) => (
                  <Box key={g.queueName} sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 0.75 }}>
                    <Box sx={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      pb: 0.5, borderBottom: "3px solid", borderColor: g.queueColor || "divider"
                    }}>
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ color: g.queueColor || "text.primary" }}>
                        {g.queueName}
                      </Typography>
                      <Box sx={{
                        fontSize: 13, fontWeight: 700, color: "text.secondary",
                        bgcolor: "action.hover", borderRadius: 999, px: 1
                      }}>
                        {g.tickets.length}
                      </Box>
                    </Box>
                    {g.tickets.map((t) => (
                      <TicketCard t={t} key={t.ticketId} onClick={() => history.push(`/tickets/${t.ticketId}`)} />
                    ))}
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Paper>

        {/* Fix 2026-08-19: linha de baixo com 5 cards - Agenda de hoje
            entrou no lugar de Tarefas (que nao tinha conteudo real
            ainda), ganhando coluna mais larga que as outras 4 (tem mais
            texto por linha: horario + setor + titulo + responsavel).
            Grafico + legenda lado a lado nos 3 cards com donut, em vez
            de empilhado, pra caber numa altura fixa e mais baixa.
            Fix 2026-09-10: gridTemplateColumns estava fixo em 5 colunas
            (nao usava o "bottomTemplate" ja calculado acima a partir dos
            modulos visiveis) - com 3 modulos novos (8 no total,
            escolhidos um a um pela gaveta), precisa ser dinamico de
            verdade pra caber sem sobrar/faltar coluna. */}
        <Box sx={{ display: "grid", gridTemplateColumns: bottomTemplate || "1fr", gap: 2, flexShrink: 0, height: 210 }}>
          <Paper sx={{ p: 1.5, height: "100%", overflow: "hidden", display: isVisible(modules, "attendants") ? "flex" : "none", flexDirection: "column" }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.75 }}>Quem está atendendo</Typography>
            <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden", }}>
              {(attendants || []).map((a) => (
                <Box key={a.id} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 0.6, borderBottom: "1px solid", borderColor: "divider", gap: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: a.online ? "success.main" : "grey.700", flexShrink: 0 }} />
                    <Typography variant="body2" noWrap>{a.name}</Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexShrink: 0 }}>
                    {a.avgSlaMinutes != null && (
                      <Typography variant="caption" color="text.secondary" noWrap>
                        SLA {a.avgSlaMinutes}m
                      </Typography>
                    )}
                    {a.overloaded ? (
                      <WarningAmberIcon fontSize="small" color="error" titleAccess="Sobrecarregado" />
                    ) : (
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {a.liveOpenCount}{a.maxSimultaneous > 0 ? `/${a.maxSimultaneous}` : ""}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
              {(!attendants || attendants.length === 0) && (
                <Typography variant="body2" color="text.secondary">Ninguém online ou com atendimento hoje.</Typography>
              )}
            </Box>
          </Paper>

          <Paper sx={{ p: 1.5, height: "100%", overflow: "hidden", display: isVisible(modules, "statusChart") ? "flex" : "none", flexDirection: "column" }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.75 }}>Status geral</Typography>
            {statusPieData.length > 0 ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1, minHeight: 0 }}>
                <Box sx={{ width: 120, height: "100%", flexShrink: 0 }}>
                  <MiniPie data={statusPieData} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 0.4 }}>
                  {statusPieData.map((d) => (
                    <LegendRow key={d.name} color={d.color} name={d.name} value={d.value} />
                  ))}
                </Box>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">Sem atendimentos hoje ainda.</Typography>
            )}
          </Paper>

          <Paper sx={{ p: 1.5, height: "100%", overflow: "hidden", display: isVisible(modules, "sectorChart") ? "flex" : "none", flexDirection: "column" }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.75 }}>Atendimentos por setor</Typography>
            {sectorPieData.length > 0 ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1, minHeight: 0 }}>
                <Box sx={{ width: 120, height: "100%", flexShrink: 0 }}>
                  <MiniPie data={sectorPieData} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, overflowY: "auto", overflowX: "hidden", display: "flex", flexDirection: "column", gap: 0.5 }}>
                  {(perSector || []).map((s) => (
                    <Box key={s.queueId}>
                      <Typography variant="caption" fontWeight="bold" sx={{ color: s.color || "text.primary", display: "block", fontSize: 12 }} noWrap>
                        {s.queueName}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 0.4, mt: 0.25 }}>
                        <Chip size="small" color="warning" label={s.pendingCount} sx={{ minWidth: 20, height: 16, fontSize: 10, fontWeight: 600, "& .MuiChip-label": { px: 0.6 } }} />
                        <Chip size="small" color="primary" label={s.openCount} sx={{ minWidth: 20, height: 16, fontSize: 10, fontWeight: 600, "& .MuiChip-label": { px: 0.6 } }} />
                        <Chip size="small" color="success" label={s.closedCount} sx={{ minWidth: 20, height: 16, fontSize: 10, fontWeight: 600, "& .MuiChip-label": { px: 0.6 } }} />
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">Sem atendimentos hoje ainda.</Typography>
            )}
          </Paper>

          <Paper sx={{ p: 1.5, height: "100%", overflow: "hidden", display: isVisible(modules, "reasonsChart") ? "flex" : "none", flexDirection: "column" }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.75 }}>Principais motivos</Typography>
            {reasonsPieData.length > 0 ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1, minHeight: 0 }}>
                <Box sx={{ width: 120, height: "100%", flexShrink: 0 }}>
                  <MiniPie data={reasonsPieData} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, overflowY: "auto", overflowX: "hidden", display: "flex", flexDirection: "column", gap: 0.4 }}>
                  {reasonsPieData.map((d) => (
                    <LegendRow key={d.name} color={d.color} name={d.name} value={d.value} />
                  ))}
                </Box>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">Nenhum atendimento com motivo hoje.</Typography>
            )}
          </Paper>

          {/* Agenda de hoje - Fix 2026-08-19: veio pro lugar de Tarefas,
              puxando compromissos reais (Appointments, ja sincronizados
              com o Google Calendar quando conectado). Filtro por setor
              escondido atras da engrenagem, so aparece se clicada. */}
          <Paper sx={{ p: 1.5, height: "100%", overflow: "hidden", display: isVisible(modules, "agenda") ? "flex" : "none", flexDirection: "column" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
              <Typography variant="subtitle1" fontWeight="bold">📅 Agenda de hoje</Typography>
              <IconButton size="small" onClick={() => setAgendaFilterOpen((p) => !p)} sx={{ p: 0.5 }}>
                <SettingsIcon fontSize="small" color={agendaFilterOpen ? "primary" : "action"} />
              </IconButton>
            </Box>
            {agendaFilterOpen && (
              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mb: 0.75 }}>
                <Chip
                  size="small"
                  label="Todos"
                  onClick={() => setAgendaFilterQueueId(null)}
                  color={!agendaFilterQueueId ? "primary" : "default"}
                  sx={{ height: 20, fontSize: 11 }}
                />
                {(perSector || []).map((s) => (
                  <Chip
                    key={s.queueId}
                    size="small"
                    label={s.queueName}
                    onClick={() => setAgendaFilterQueueId(s.queueId)}
                    sx={{
                      height: 20, fontSize: 11,
                      bgcolor: agendaFilterQueueId === s.queueId ? (s.color || "primary.main") : "action.hover",
                      color: agendaFilterQueueId === s.queueId ? "#fff" : "text.secondary",
                    }}
                  />
                ))}
              </Box>
            )}
            <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden", display: "flex", flexDirection: "column", gap: 0.6 }}>
              {filteredAppointments.length === 0 ? (
                <Typography variant="body2" color="text.secondary">Nenhum compromisso hoje.</Typography>
              ) : (
                filteredAppointments.map((ap) => (
                  <Box
                    key={ap.id}
                    sx={{
                      borderLeft: "4px solid", borderColor: ap.queue?.color || "primary.main",
                      bgcolor: "action.hover", borderRadius: 1, px: 1, py: 0.5
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75 }}>
                      <Typography variant="body2" fontWeight="bold">{moment(ap.startAt).format("HH:mm")}</Typography>
                      {ap.queue?.name && (
                        <Typography variant="caption" fontWeight="bold" sx={{ color: ap.queue?.color || "text.secondary", textTransform: "uppercase" }}>
                          {ap.queue.name}
                        </Typography>
                      )}
                      <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>{ap.title}</Typography>
                    </Box>
                    {ap.user?.name && (
                      <Typography variant="caption" color="text.secondary">Responsável: {ap.user.name}</Typography>
                    )}
                  </Box>
                ))
              )}
            </Box>
          </Paper>

          {/* Recorrencia hoje - contatos/organizacoes com mais de um
              ticket hoje (2026-09-10). */}
          <Paper sx={{ p: 1.5, height: "100%", overflow: "hidden", display: isVisible(modules, "recurrence") ? "flex" : "none", flexDirection: "column" }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.75 }}>Recorrência hoje</Typography>
            <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden", display: "flex", flexDirection: "column", gap: 0.5 }}>
              {(recurrenceToday || []).length === 0 ? (
                <Typography variant="body2" color="text.secondary">Nenhuma recorrência hoje.</Typography>
              ) : (
                recurrenceToday.map((r, i) => (
                  <Box key={i} sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                    <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>{r.name}</Typography>
                    <Typography variant="body2" fontWeight="bold" sx={{ flexShrink: 0 }}>{r.ticketCount}</Typography>
                  </Box>
                ))
              )}
            </Box>
          </Paper>

          {/* Tarefas em aberto da empresa toda (nao "minhas", visao geral
              pra TV) - 2026-09-10. Fix 2026-10-02: "preciso conseguir
              ver de uma forma melhor" - titulo maior (h6), texto em
              body1/body2 em vez de body2/caption, e line-clamp de 2
              linhas no lugar de noWrap (que cortava o titulo da tarefa
              no meio com "..." numa coluna estreita). */}
          <Paper sx={{ p: 1.5, height: "100%", overflow: "hidden", display: isVisible(modules, "tasks") ? "flex" : "none", flexDirection: "column" }}>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>Tarefas em aberto</Typography>
            <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden", display: "flex", flexDirection: "column", gap: 1.25 }}>
              {(openTasks || []).length === 0 ? (
                <Typography variant="body2" color="text.secondary">Nenhuma tarefa em aberto.</Typography>
              ) : (
                openTasks.map((tk) => (
                  <Box key={tk.id} sx={{ borderLeft: "4px solid", borderColor: tk.overdue ? "error.main" : "divider", pl: 1 }}>
                    <Typography
                      variant="body1"
                      fontWeight={tk.overdue ? "bold" : "normal"}
                      color={tk.overdue ? "error.main" : "text.primary"}
                      sx={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}
                    >
                      {tk.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {tk.assignedUserName || "Sem responsável"}
                      {tk.dueDate ? ` · ${moment(tk.dueDate).format("DD/MM HH:mm")}` : ""}
                    </Typography>
                  </Box>
                ))
              )}
            </Box>
          </Paper>

          {/* Resumos de IA dos tickets encerrados hoje (Ticket.closingSummary)
              - 2026-09-10. */}
          <Paper sx={{ p: 1.5, height: "100%", overflow: "hidden", display: isVisible(modules, "aiSummaries") ? "flex" : "none", flexDirection: "column" }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.75 }}>Resumos de IA — encerrados hoje</Typography>
            <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden", display: "flex", flexDirection: "column", gap: 0.6 }}>
              {(recentClosedSummaries || []).length === 0 ? (
                <Typography variant="body2" color="text.secondary">Nenhum encerramento com resumo hoje.</Typography>
              ) : (
                recentClosedSummaries.map((s) => (
                  <Box key={s.ticketId}>
                    <Typography variant="caption" fontWeight="bold" noWrap sx={{ display: "block" }}>{s.contactName}</Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}
                    >
                      {s.summary}
                    </Typography>
                  </Box>
                ))
              )}
            </Box>
          </Paper>

          {/* Lista de pendencias, nao so a contagem - pedido do usuario:
              "mostra a quantidade mas nao mostra quais sao, isso nao
              serve pra mim" (2026-09-11). Cada item e um card na coluna
              Pendencias de qualquer funil, mais antigo primeiro.
              Fix 2026-10-02: "preciso conseguir ver de uma forma
              melhor" - titulo maior (h6), nome do contato em body1,
              fila numa linha propria (antes dividia espaco com o
              resumo, os dois cortados com "...") e resumo com
              line-clamp de 2 linhas em vez de noWrap. */}
          <Paper sx={{ p: 1.5, height: "100%", overflow: "hidden", display: isVisible(modules, "pendenciasList") ? "flex" : "none", flexDirection: "column" }}>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>Pendências</Typography>
            <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden", display: "flex", flexDirection: "column", gap: 1.25 }}>
              {(kanbanPendingList || []).length === 0 ? (
                <Typography variant="body2" color="text.secondary">Nenhuma pendência em aberto.</Typography>
              ) : (
                kanbanPendingList.map((p) => (
                  <Box
                    key={p.id}
                    sx={{ borderLeft: "4px solid", borderColor: p.queueColor || "warning.main", pl: 1 }}
                  >
                    <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75 }}>
                      <Typography variant="body1" fontWeight="bold" noWrap sx={{ flex: 1, minWidth: 0 }}>
                        {p.contactName || p.title || "Sem título"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
                        {formatElapsed(moment().diff(moment(p.enteredPhaseAt), "minutes"))}
                      </Typography>
                    </Box>
                    {p.queueName && (
                      <Typography variant="caption" fontWeight="bold" sx={{ color: p.queueColor || "text.secondary", textTransform: "uppercase", display: "block" }}>
                        {p.queueName}
                      </Typography>
                    )}
                    {p.summary && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}
                      >
                        {p.summary}
                      </Typography>
                    )}
                  </Box>
                ))
              )}
            </Box>
          </Paper>
        </Box>
      </Box>
      </Box>

      {/* Gaveta de escolha dos modulos. Renderiza num portal, fora do
          canvas virtual de 1920x1080 que sofre o transform: scale - ou
          seja, aparece no tamanho real do navegador e fica legivel
          mesmo numa TV 4K, onde o canvas todo esta reduzido. */}
      <Drawer anchor="right" open={modulesOpen} onClose={() => setModulesOpen(false)}>
        <Box sx={{ width: 330, p: 2 }}>
          <Typography variant="h6" fontWeight="bold">Módulos do painel</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Vale para toda a empresa. As telas já abertas se ajustam em até 30 segundos.
          </Typography>
          <Divider sx={{ mb: 1 }} />
          {MODULE_GROUPS.map((group) => (
            <Box key={group} sx={{ mb: 1.5 }}>
              <Typography
                variant="caption"
                fontWeight="bold"
                color="text.secondary"
                sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
              >
                {group}
              </Typography>
              {MODULE_DEFS.filter((m) => m.group === group).map((m) => (
                <FormControlLabel
                  key={m.key}
                  sx={{ display: "block", ml: 0 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={isVisible(modules, m.key)}
                      onChange={() => toggleModule(m.key)}
                    />
                  }
                  label={<Typography variant="body2">{m.label}</Typography>}
                />
              ))}
            </Box>
          ))}
        </Box>
      </Drawer>
    </ThemeProvider>
  );
};

export default PainelTv;
